/**
 * Comment Store - Manages comment state for the Billing domain
 *
 * This store replaces module-level Maps with reactive Zustand state,
 * enabling proper synchronization across all component instances.
 *
 * Part of the comment system redesign to fix:
 * - Race conditions from module-level global caches
 * - Cache synchronization issues between hook instances
 * - 70+ individual Firestore subscriptions (reduced to 1 page-level subscription)
 *
 * @see docs/comment-system/architecture-design.md
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import { log } from '@/lib/logger';
import {
  collection,
  query,
  where,
  onSnapshot,
  type Timestamp,
  type DocumentData
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

// Enable Immer's Map and Set support - required for using Maps in the store
enableMapSet();

const CHANNELS_COLLECTION = 'chat_channels';

/**
 * Convert Firestore document to ChatChannelInstance
 */
function documentToChannel(doc: DocumentData): ChatChannelInstance {
  const data = doc.data();
  
  // Determine isActive: prefer explicit isActive, fallback to inverse of isArchived
  const isActive = data.isActive !== undefined
    ? data.isActive
    : (data.isArchived !== undefined ? !data.isArchived : true);
  
  return {
    channelId: data.channelId || doc.id,
    workflowId: data.workflowId,
    entryId: data.entryId,
    isActive,
    messageCount: data.messageCount ?? 0,
    activeMessageCount: data.activeMessageCount,
    totalMessageCount: data.totalMessageCount,
    participants: data.participants ?? [],
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

/**
 * Chat message structure for modal display
 * Preserved from existing useSpendRowChat.ts for compatibility
 */
export interface ChatMessage {
  id: string;
  text: string;
  createdAt: Date;
  clientId: string;
  metadata?: {
    userName?: string;
    userRole?: string;
    userAvatar?: string;
    mentions?: string[];
    timestamp?: string;
  };
  reactions?: Record<string, string[]>;
  isResolved?: boolean;
  resolvedAt?: Date;
  resolvedBy?: string;
}

/**
 * Chat channel instance from Firestore
 * Used by the page-level subscription to track counts
 */
export interface ChatChannelInstance {
  channelId: string;
  workflowId: string;
  entryId: string;
  isActive: boolean;
  messageCount: number;
  activeMessageCount?: number;
  totalMessageCount?: number;
  participants: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Comment state interface
 */
interface CommentState {
  /**
   * Map of entryId -> active message count
   * Updated by page-level subscription to chat_channels
   */
  countMap: Map<string, number>;
  
  /**
   * Map of channelId -> messages for open modals
   * Populated when a modal opens, cleared when it closes
   */
  messageCache: Map<string, ChatMessage[]>;
  
  /**
   * Currently subscribed workflowIds
   * Used to track which workflows the subscription is monitoring
   */
  subscribedWorkflowIds: string[] | null;
  
  /**
   * Subscription cleanup function
   * Called when workflowIds change or component unmounts
   */
  unsubscribe: (() => void) | null;
  
  /**
   * Loading state for the page-level subscription
   */
  isLoading: boolean;
  
  /**
   * Error state for subscription failures
   */
  error: string | null;
  
  // Actions

  /**
   * Set the unsubscribe function for cleanup
   */
  setUnsubscribe: (unsubscribe: (() => void) | null) => void;
  
  /**
   * Full cleanup - clears state and calls unsubscribe
   * Use when completely leaving the page
   */
  cleanup: () => void;
  
  /**
   * Soft cleanup - only unsubscribes but preserves countMap
   * Use when provider remounts but data should persist
   */
  softCleanup: () => void;
  
  /**
   * Update counts from Firestore channel documents
   * Called by useCommentSubscription when channels change
   */
  updateCounts: (channels: ChatChannelInstance[]) => void;
  
  /**
   * Set loading state
   */
  setLoading: (loading: boolean) => void;
  
  /**
   * Set error state
   */
  setError: (error: string | null) => void;
  
  /**
   * Set messages for a specific channel (when modal loads)
   */
  setMessages: (channelId: string, messages: ChatMessage[]) => void;
  
  /**
   * Clear messages for a specific channel (when modal closes or resolves)
   */
  clearMessages: (channelId: string) => void;
  
  /**
   * Update count for a specific entry (for optimistic updates)
   */
  updateEntryCount: (entryId: string, count: number) => void;
  
  /**
   * Increment count for a specific entry (when sending a message)
   */
  incrementEntryCount: (entryId: string) => void;
  
  /**
   * Reset count for a specific entry to zero (when resolving)
   */
  resetEntryCount: (entryId: string) => void;
  
  /**
   * Setup Firestore subscription for workflow channels
   * Handles all subscription logic including cleanup of existing subscriptions
   */
  setupSubscription: (workflowIds: string[]) => void;
  
  /**
   * Teardown the current subscription
   * Preserves countMap for UI stability
   */
  teardownSubscription: () => void;
}

/**
 * Create a stable key from workflow IDs for comparison
 */
function getWorkflowKey(workflowIds: string[]): string {
  return [...workflowIds].sort().join(',');
}

/**
 * Create the comment store with immer for immutable updates
 */
export const useCommentStore = create<CommentState>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      countMap: new Map(),
      messageCache: new Map(),
      subscribedWorkflowIds: null,
      unsubscribe: null,
      isLoading: false,
      error: null,
      
      // Actions
      setUnsubscribe: (unsubscribe: (() => void) | null) => {
        set((state) => {
          state.unsubscribe = unsubscribe;
        });
      },
      
      cleanup: () => {
        const { unsubscribe } = get();
        
        // Call unsubscribe if exists
        if (unsubscribe) {
          try {
            unsubscribe();
          } catch (error) {
            log.warn('[COMMENT-STORE] Error during unsubscribe:', error);
          }
        }
        
        set((state) => {
          state.countMap = new Map();
          state.messageCache = new Map();
          state.subscribedWorkflowIds = null;
          state.unsubscribe = null;
          state.isLoading = false;
          state.error = null;
        });
      },
      
      softCleanup: () => {
        const { unsubscribe } = get();
        
        // Only unsubscribe, preserve countMap for UI stability
        if (unsubscribe) {
          try {
            unsubscribe();
          } catch (error) {
            log.warn('[COMMENT-STORE] Error during soft unsubscribe:', error);
          }
        }
        
        set((state) => {
          // Preserve countMap - don't clear it!
          // This prevents icons from flickering when provider remounts
          state.unsubscribe = null;
          // Clear subscribedWorkflowIds so initializeForWorkflows() will re-subscribe on remount
          state.subscribedWorkflowIds = null;
          state.isLoading = false;
        });
      },
      
      updateCounts: (channels: ChatChannelInstance[]) => {
        
        set((state) => {
          // ATOMIC REPLACEMENT - create fresh map from query results only
          // This ensures resolved channels (excluded from query) are removed
          const newCountMap = new Map<string, number>();
          
          for (const channel of channels) {
            if (channel.entryId && channel.isActive) {
              // Use activeMessageCount if available, fallback to messageCount, then 0
              const count = channel.activeMessageCount ?? channel.messageCount ?? 0;
              newCountMap.set(channel.entryId, count);
            }
          }
          
          state.countMap = newCountMap;
          state.isLoading = false;
          state.error = null;
        });
      },
      
      setLoading: (loading: boolean) => {
        set((state) => {
          state.isLoading = loading;
        });
      },
      
      setError: (error: string | null) => {
        set((state) => {
          state.error = error;
          state.isLoading = false;
        });
      },
      
      setMessages: (channelId: string, messages: ChatMessage[]) => {
        
        set((state) => {
          state.messageCache.set(channelId, messages);
        });
      },
      
      clearMessages: (channelId: string) => {
        
        set((state) => {
          state.messageCache.delete(channelId);
        });
      },
      
      updateEntryCount: (entryId: string, count: number) => {
        set((state) => {
          state.countMap.set(entryId, count);
        });
      },
      
      incrementEntryCount: (entryId: string) => {
        set((state) => {
          const currentCount = state.countMap.get(entryId) ?? 0;
          state.countMap.set(entryId, currentCount + 1);
        });
      },
      
      resetEntryCount: (entryId: string) => {
        
        set((state) => {
          state.countMap.set(entryId, 0);
        });
      },
      
      setupSubscription: (workflowIds: string[]) => {
        const { subscribedWorkflowIds, unsubscribe: existingUnsubscribe } = get();
        
        const newKey = getWorkflowKey(workflowIds);
        const currentKey = subscribedWorkflowIds ? getWorkflowKey(subscribedWorkflowIds) : '';
        
        // Already subscribed with active listener? Skip.
        // This is the KEY check - we verify BOTH the key AND the unsubscribe function
        if (currentKey === newKey && existingUnsubscribe !== null) {
          return;
        }
        
        // Clean up existing subscription if present
        if (existingUnsubscribe) {
          try {
            existingUnsubscribe();
          } catch (error) {
            log.warn('[COMMENT-STORE] Error during subscription cleanup:', error);
          }
        }
        
        // Update state to indicate we're setting up
        set((state) => {
          state.subscribedWorkflowIds = workflowIds;
          state.isLoading = true;
          state.error = null;
          // Clear countMap when switching workflows
          if (currentKey !== newKey) {
            state.countMap = new Map();
          }
        });
        
        // Firestore 'in' query supports max 30 values - chunk if needed
        const FIRESTORE_IN_LIMIT = 30;
        const chunks: string[][] = [];
        for (let i = 0; i < workflowIds.length; i += FIRESTORE_IN_LIMIT) {
          chunks.push(workflowIds.slice(i, i + FIRESTORE_IN_LIMIT));
        }

        // Track all channels across chunks for atomic count update
        const channelsByChunk = new Map<number, ChatChannelInstance[]>();
        const unsubscribeFns: Array<() => void> = [];

        const handleUpdate = () => {
          const allChannels: ChatChannelInstance[] = [];
          for (const channels of channelsByChunk.values()) {
            allChannels.push(...channels);
          }
          get().updateCounts(allChannels);
        };

        chunks.forEach((chunk, chunkIndex) => {
          const channelsRef = collection(db, CHANNELS_COLLECTION);
          const q = query(
            channelsRef,
            where('workflowId', 'in', chunk),
            where('isActive', '==', true)
          );

          const unsubFn = onSnapshot(
            q,
            (snapshot) => {
              channelsByChunk.set(chunkIndex, snapshot.docs.map(documentToChannel));
              handleUpdate();
            },
            (error) => {
              log.error('[COMMENT-STORE] Subscription error:', error);
              set((state) => {
                state.error = error.message || 'Failed to subscribe to comment channels';
                state.isLoading = false;
              });
            }
          );
          unsubscribeFns.push(unsubFn);
        });

        const newUnsubscribe = () => unsubscribeFns.forEach(fn => fn());

        // Store the unsubscribe function
        set((state) => {
          state.unsubscribe = newUnsubscribe;
        });
      },
      
      teardownSubscription: () => {
        const { unsubscribe } = get();
        
        if (unsubscribe) {
          try {
            unsubscribe();
          } catch (error) {
            log.warn('[COMMENT-STORE] Error during subscription teardown:', error);
          }
        }
        
        set((state) => {
          state.unsubscribe = null;
          // Clear subscribedWorkflowIds so setupSubscription knows to re-subscribe
          state.subscribedWorkflowIds = null;
          // Preserve countMap for UI stability - icons don't flicker
          state.isLoading = false;
        });
      },
    })),
    { name: 'comment-store' }
  )
);

// ============================================================================
// Selectors - For efficient component subscriptions
// ============================================================================

/**
 * Select count for a specific entry
 * Use this in components to avoid re-renders when other entries change
 */
export const selectCommentCount = (entryId: string) => 
  (state: CommentState): number => state.countMap.get(entryId) ?? 0;

/**
 * Select whether workflow(s) are currently subscribed
 */
export const selectIsSubscribed = (workflowIds: string[]) =>
  (state: CommentState): boolean => {
    if (!state.subscribedWorkflowIds) return false;
    const currentKey = getWorkflowKey(workflowIds);
    const subscribedKey = getWorkflowKey(state.subscribedWorkflowIds);
    return currentKey === subscribedKey;
  };

// ============================================================================
// Utility functions
// ============================================================================

/**
 * Get current comment count for an entry (non-reactive, for one-off reads)
 */
export const getCommentCount = (entryId: string): number => {
  return useCommentStore.getState().countMap.get(entryId) ?? 0;
};

/**
 * Check if store is ready for specific workflow(s)
 */
export const isStoreReady = (workflowIds: string[]): boolean => {
  const state = useCommentStore.getState();
  if (!state.subscribedWorkflowIds) return false;
  const currentKey = getWorkflowKey(workflowIds);
  const subscribedKey = getWorkflowKey(state.subscribedWorkflowIds);
  return currentKey === subscribedKey && !state.isLoading;
};

